import { observer } from "mobx-react-lite";
import {
    PayloadElement,
    PayloadElementType,
    PayloadStructure,
} from "../types/Actions";
import {
    Button,
    Form,
    Input,
    Segmented,
    Select,
    Space,
    Tree,
    TreeDataNode,
} from "antd";
import React, { useState } from "react";
import {
    MinusCircleTwoTone,
    PlusCircleOutlined,
    PlusCircleTwoTone,
} from "@ant-design/icons";
import ActionDefinitionStore from "../stores/ActionDefinitionStore";

type PayloadStructureFormProps = {
    store: ActionDefinitionStore;
};

type PayloadElementProps = {
    name?: string;
    type?: PayloadElementType;
    field?: string;
    children?: PayloadElement[];
};

type PayloadStructTreeDataNode = TreeDataNode & {
    element: PayloadElement;
    parent?: PayloadElement;
};

const PayloadStructureForm: React.FC<PayloadStructureFormProps> = ({
    store,
}) => {
    const [treeData, setTreeData] = useState<PayloadStructure>({
        elements: [],
    });

    React.useEffect(() => {
        if (!store.editPayloadDialogVisible) {
            setTreeData({ elements: [] });
        } else {
            setTreeData(store.selectedPayloadStructure ?? { elements: [] });
        }
    }, [store.editPayloadDialogVisible, store.selectedPayloadStructure]);

    const addNode = (parentNode?: PayloadElement) => {
        const newNode: PayloadElement = {
            name: undefined,
            type: "Property",
            children: [],
        };

        const addChild = (elements: PayloadElement[]): PayloadElement[] =>
            elements.map((element) => {
                if (element === parentNode) {
                    return {
                        ...element,
                        children: [...(element.children || []), newNode],
                    };
                }
                return {
                    ...element,
                    children: addChild(element.children || []),
                };
            });

        setTreeData({
            elements: parentNode
                ? addChild(treeData.elements)
                : [...treeData.elements, newNode],
        });
    };

    const updateNode = (
        node: PayloadElement,
        updatedProps: PayloadElementProps,
    ) => {
        const updateName = (elements: PayloadElement[]): PayloadElement[] =>
            elements.map((element) => {
                if (element === node) {
                    return { ...element, ...updatedProps };
                }
                return {
                    ...element,
                    children: updateName(element.children || []),
                };
            });

        setTreeData({ elements: updateName(treeData.elements) });
    };

    const removeNode = (node: PayloadElement) => {
        const removeNode = (elements: PayloadElement[]): PayloadElement[] =>
            elements
                .filter((element) => element !== node)
                .map((element) => ({
                    ...element,
                    children: removeNode(element.children || []),
                }));

        setTreeData({ elements: removeNode(treeData.elements) });
    };

    const handleSave = () => {
        if (store.payloadStructureChangeCallback) {
            store.payloadStructureChangeCallback(
                treeData.elements.length > 0 ? treeData : null,
            );
        }
        store.setEditPayloadDialogVisible(false, null);
    };

    const renderNode = (node: PayloadStructTreeDataNode) => {
        const { element, parent } = node;

        return (
            <Space>
                <Segmented<PayloadElementType>
                    size="small"
                    options={[
                        { value: "Property", label: '" "' },
                        { value: "Array", label: "[ ]" },
                        { value: "Object", label: "{ }" },
                    ]}
                    value={element.type}
                    onChange={(type) => {
                        let updateObj: PayloadElementProps = { type };
                        if (type !== "Property") {
                            updateObj = {
                                ...updateObj,
                                field: undefined,
                            };
                        } else {
                            updateObj = {
                                ...updateObj,
                                children: [],
                            };
                        }

                        if (type === "Array") {
                            updateObj = {
                                ...updateObj,
                                children: element.children?.map((child) => ({
                                    ...child,
                                    name: undefined,
                                })),
                            };
                        }
                        updateNode(element, updateObj);
                    }}
                />
                <Input
                    size="small"
                    placeholder="Element name"
                    value={element.name}
                    onChange={(e) =>
                        updateNode(element, {
                            name: e.target.value,
                        })
                    }
                    disabled={parent?.type === "Array"}
                    style={{ width: "180px" }}
                />
                <Select
                    size="small"
                    value={element.field}
                    placeholder="Field"
                    style={{ width: "180px" }}
                    options={store.payloadStructureFields?.map((f) => ({
                        value: f,
                        label: f,
                    }))}
                    onChange={(field) => {
                        let updateObj: PayloadElementProps = { field };

                        if (!element.name && parent?.type !== "Array") {
                            updateObj = {
                                ...updateObj,
                                name: field,
                            };
                        }
                        updateNode(element, updateObj);
                    }}
                    disabled={element.type !== "Property"}
                />
                <PlusCircleTwoTone
                    twoToneColor={
                        element.type === "Property" ? "#aaa" : "green"
                    }
                    onClick={() => {
                        if (
                            element.type === "Array" ||
                            element.type === "Object"
                        ) {
                            addNode(element);
                        }
                    }}
                />
                <MinusCircleTwoTone
                    twoToneColor="red"
                    onClick={() => removeNode(element)}
                />
            </Space>
        );
    };

    const getTreeData = () => {
        const mapElements = (
            elements: PayloadElement[],
            parentIndex: string = "",
            parentElement?: PayloadElement,
        ): PayloadStructTreeDataNode[] => {
            return elements.map((e, index) => ({
                key: `${parentIndex}${index}`,
                element: e,
                parent: parentElement,
                isLeaf: !e.children || e.children.length === 0,
                children: e.children
                    ? mapElements(e.children, `${parentIndex}${index}-`, e)
                    : [],
            }));
        };
        return mapElements(treeData.elements);
    };

    return (
        <div>
            <Form onFinish={handleSave} id="edit-payload-structure-form" />
            <Button
                size="small"
                onClick={() => addNode()}
                style={{ marginBottom: "8px" }}
                icon={<PlusCircleOutlined />}
            >
                Add root element
            </Button>
            <Tree
                showLine
                selectable={false}
                autoExpandParent
                treeData={getTreeData()}
                titleRender={renderNode}
            />
        </div>
    );
};

export default observer(PayloadStructureForm);
